import { useState } from 'react'
import { isApiError } from '../../api/client'
import { Button, EmptyState, Pager, Spinner } from '../../design'
import { canManageKnowledge } from '../../session/roles'
import { useReadySession } from '../../session/useSession'
import { AddSourceForm } from './AddSourceForm'
import { KB_COPY } from './copy'
import { KbSandbox } from './KbSandbox'
import { KbSourceList } from './KbSourceList'
import { useAddKbSource, useDeleteKbSource, useKbSources } from './queries'
import { useKbDownload } from './useKbDownload'
import { useKbRetry } from './useKbRetry'

const FIRST_PAGE = 1

export const KnowledgePage = () => {
  const { membership } = useReadySession()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [page, setPage] = useState(FIRST_PAGE)
  const sources = useKbSources(page)
  const add = useAddKbSource()
  const remove = useDeleteKbSource()
  const retry = useKbRetry()
  const download = useKbDownload()

  if (!canManageKnowledge(membership.role)) {
    return <EmptyState title={KB_COPY.forbidden} description={KB_COPY.forbiddenBody} />
  }

  if (sources.isPending) return <Spinner label={KB_COPY.loading} />

  if (sources.isError) {
    return (
      <EmptyState
        title={KB_COPY.loadFailed}
        description={isApiError(sources.error) ? sources.error.message : ''}
        action={
          <Button variant="primary" onClick={() => void sources.refetch()}>
            {KB_COPY.retry}
          </Button>
        }
      />
    )
  }

  const { sources: rows, meta } = sources.data
  const pageCount = Math.max(FIRST_PAGE, Math.ceil(meta.total / meta.limit))

  return (
    <section className="kb">
      <h1>{KB_COPY.title}</h1>
      <div className="kb-panel">
        <KbSourceList
          sources={rows}
          deletingId={deletingId}
          {...retry}
          download={download}
          onDelete={(id) => {
            setDeletingId(id)
            remove.mutate(id, { onSettled: () => setDeletingId(null) })
          }}
        />
        <Pager
          label={KB_COPY.pagination}
          status={KB_COPY.pageStatus(meta.page, meta.total, meta.limit)}
          previousLabel={KB_COPY.previousPage}
          nextLabel={KB_COPY.nextPage}
          previousDisabled={page <= FIRST_PAGE}
          nextDisabled={page >= pageCount}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      </div>
      <AddSourceForm
        pending={add.isPending}
        error={isApiError(add.error) ? add.error.message : null}
        onSubmit={(input) => add.mutate(input, { onSuccess: () => setPage(FIRST_PAGE) })}
      />
      <KbSandbox />
    </section>
  )
}
